import {useRef, useState, useCallback, useEffect, useLayoutEffect} from 'react';
import {useSpring} from '@react-spring/web';
import {useDrag} from '@use-gesture/react';
import {clamp} from 'utils';

function useElementWidth(ref: any) {
    const [width, setWith] = useState(
        window?.innerWidth || 0
    );

    useLayoutEffect(() => {
        function updateSize() {
          setWith(ref.current?.offsetWidth || 0);
        }

        window.addEventListener('resize', updateSize);
        updateSize();

        return () => window.removeEventListener('resize', updateSize);
    }, [width, ref]);

    return width
}

export function useSlider({containerRef, initialSlide = 0, slides}: any) {
    const index = useRef(initialSlide);
    // Slide numbers (for display purposes only)
    const [currentSlide, updateSlide] = useState(initialSlide);
    const [zooming, setZooming] = useState(false);

    const containerWidth = useElementWidth(containerRef);
    // react-spring v9: useSpring(fn) returns [styles, api]; drive with api.start(...)
    const [{x, scale}, api] = useSpring(() => ({
        x: containerWidth * index.current,
        scale: 1,
        config: {
            tension: 270,
            clamp: true,
        },
    }));

    useEffect(() => {
      api.start({
          x: -currentSlide * containerWidth
      })
    }, [api, currentSlide, containerWidth])

    const onScale = useCallback(
        (slideProps: any) => {
            api.start({scale: slideProps.scale});
            if (slideProps.scale === 1) {
                setZooming(false);
            } else {
                setZooming(true);
            }
        },
        [api]
    );

    const clampSlides = (idx: number) => clamp(idx, 0, slides.length - 1);
    const bind = useDrag(
        ({
            down,
            movement: [movementX],
            direction: [directionX],
            distance,
            swipe: [swipeX],
            cancel,
            touches,
        }: any) => {
            // We don't want to interrupt the pinch-to-zoom gesture
            if (touches > 1) {
                cancel();
            }

            // @use-gesture v10: distance is a vector [dx, dy]; use the x component.
            const distanceX = Array.isArray(distance) ? distance[0] : distance;

            // We have swiped past halfway
            if (!down && distanceX > containerWidth / 2) {
                index.current = clampSlides(index.current - directionX);
            } else if (swipeX !== 0) {
                // We have detected a swipe - update the new index
                index.current = clampSlides(index.current - swipeX);
            }

            // Animate the transition
            api.start({
                x: -index.current * containerWidth + (down ? movementX : 0),
                immediate: down,
            });

            // Update the slide number for display purposes
            updateSlide(index.current);
        },
        {
            axis: 'x',
            bounds: {
                left: currentSlide === slides.length - 1 ? 0 : -Infinity,
                right: index.current === 0 ? 0 : Infinity,
                top: 0,
                bottom: 0,
            },
            rubberband: true,
            enabled: slides.length > 1,
        }
    );

    return {zooming, scale, currentSlide, bind, x, onScale, updateSlide};
}
